/**
 * O MAPA ÚNICO do nome de registro: tipo → rota, chave e módulo de leitura (item 09 do lote
 * de 24/09 · D-136 · DS-533).
 *
 * O fundador: o nome de um registro que aparece numa tela leva à ficha dele, SEMPRE em nova
 * aba; sem permissão de ver o módulo, ou sem o id, fica texto puro, nunca link morto; a
 * própria ficha não se linka. Havia três mapas tipo → rota, e eles discordavam: a inbox
 * mandava a versão de orçamento com `?id=` para uma tela que só lê `?v=`, a NF ia sem id
 * nenhum, e o pedido se chamava `pedido_venda` num lugar e `pedido` no outro. Este arquivo é
 * o único dono da resposta; a busca global, a inbox, os relatórios e o `LinkDaEntidade` leem
 * daqui.
 *
 * Emendado pelo relato G1 (D-155, DS-569): dentro de uma linha de listagem o nome de OUTRO
 * registro é texto, porque a linha inteira abre o registro dela; e o registro DA PRÓPRIA
 * linha (`alvoDaLinha`) é a única exceção de aba, com o mesmo mapa e a mesma permissão.
 *
 * Puro de propósito, sem React: a suíte de unidade do web não tem DOM, e é a resolução, e
 * não a casca, que decide se o nome vira link. `link-da-entidade.tsx` só lê a permissão e o
 * endereço atual e aplica esta resolução.
 */
import {
  DESTINOS_DE_FICHA,
  type ReferenciaDeFicha,
  type TipoDeFicha,
  type TipoDeResultadoDaBusca,
  type TipoDeVinculoDaTarefa,
} from '@dv/contracts'
import type { Acao, Modulo } from '@dv/domain'
import type { Caminho } from '../rotas/caminhos.ts'

/**
 * As fichas que um nome pode abrir: as quinze que resolvem por uuid (o `tipoDeFicha` do
 * contrato) e a conta contábil, que o Plano de Contas abre pela âncora `#c-…` (o id da conta,
 * o código ou o filtro por grupo da DRE; DS-604).
 */
export type TipoDaEntidade = TipoDeFicha | 'conta_contabil'

/** Os 16 tipos, na ordem da tabela da DS-533. */
export const TIPOS_DA_ENTIDADE = [
  'empresa', 'contato', 'negocio', 'pedido', 'campanha', 'fornecedor', 'conta_pagar',
  'colaborador', 'tarefa', 'orcamento_versao', 'nf', 'regra_comissao', 'entidade_dv',
  'conta_bancaria', 'portal_externo', 'conta_contabil',
] as const satisfies readonly TipoDaEntidade[]

/** Onde a ficha lê a chave: um parâmetro de busca, ou a âncora (`#c-<código>`). */
export type ChaveDaFicha = 'id' | 'v' | 'nf' | 'hash'

export interface DestinoDoMapa {
  readonly caminho: Caminho
  readonly chave: ChaveDaFicha
  /** O módulo que o `@Exige` da rota de detalhe cobra com `ver`. */
  readonly modulo: Modulo
  /** A ficha exige ver valores no servidor (o `conta_pagar`, DS-638). */
  readonly valores?: true
  /** Parâmetro fixo que acompanha a chave (a 10.1 escolhe a categoria por `?cat=`). */
  readonly fixo?: { readonly cat: string }
}

/**
 * O mapa. Os DADOS moram nos contratos (`destinos.ts`, DS-638), que a porta das notificações
 * da API também lê: havia dois mapas, e eles discordavam no `conta_pagar`. O módulo de cada
 * linha é o do `@Exige` da rota de detalhe; aqui só se afirma o tipo de rota do roteador (o
 * teste confere cada caminho contra `CAMINHOS`).
 */
export const DESTINOS: Readonly<Record<TipoDaEntidade, DestinoDoMapa>> = DESTINOS_DE_FICHA

/** Os parâmetros de busca que o mapa escreve; todos estão em `CHAVES_DE_BUSCA`. */
export interface BuscaDaFicha {
  readonly id?: string
  readonly v?: string
  readonly nf?: string
  readonly cat?: string
  readonly scope?: string
  /** O recorte da fila de faturamento por pedido (o destino do aviso N5, DS-638). */
  readonly pedido?: string
}

/** O destino na forma do `<Link>` do roteador. */
export interface DestinoDaEntidade {
  readonly to: Caminho
  readonly search: BuscaDaFicha
  readonly hash?: string
}

export interface OpcoesDoDestino {
  /** Só para a versão de orçamento: abre a 8.2 no escopo de gastos, como a origem de gasto do caixa. */
  readonly escopoDoOrcamento?: 'gastos' | 'financeiro' | undefined
}

function vazio(id: string | null | undefined): id is null | undefined | '' {
  return id === null || id === undefined || id === ''
}

/**
 * O mapa do tipo, ou `null` para o que o mapa não conhece. O tipo chega de um `Record` tipado,
 * mas o dado vem do servidor: uma origem que o mapa da tela ainda não declara (a `pagamento_rh`
 * da Variância, AUD31-06) chegava aqui como `undefined` e derrubava a página inteira.
 */
export function mapaDoTipo(tipo: TipoDaEntidade | null | undefined): DestinoDoMapa | null {
  if (tipo === null || tipo === undefined) return null
  return (DESTINOS as Partial<Readonly<Record<string, DestinoDoMapa>>>)[tipo] ?? null
}

/** O destino da ficha, ou `null` sem tipo conhecido ou sem id: sem destino, o nome é texto. */
export function destinoDaEntidade(
  tipo: TipoDaEntidade | null | undefined,
  id: string | null | undefined,
  opcoes: OpcoesDoDestino = {},
): DestinoDaEntidade | null {
  const mapa = mapaDoTipo(tipo)
  if (mapa === null || vazio(id)) return null
  if (mapa.chave === 'hash') return { to: mapa.caminho, search: {}, hash: `c-${id}` }
  const search: { -readonly [K in keyof BuscaDaFicha]: BuscaDaFicha[K] } = {}
  if (mapa.fixo !== undefined) search.cat = mapa.fixo.cat
  search[mapa.chave] = id
  if (tipo === 'orcamento_versao' && opcoes.escopoDoOrcamento !== undefined) search.scope = opcoes.escopoDoOrcamento
  return { to: mapa.caminho, search }
}

/**
 * O mesmo destino como texto, para quem navega por string (a inbox) e para a ação de toast.
 * O id passa por `encodeURIComponent`.
 */
export function hrefDaEntidade(tipo: TipoDaEntidade | null, id: string | null | undefined): string | null {
  if (tipo === null || vazio(id)) return null
  const destino = destinoDaEntidade(tipo, id)
  if (destino === null) return null
  if (DESTINOS[tipo].chave === 'hash') return `${destino.to}#c-${encodeURIComponent(id)}`
  const partes = Object.entries(destino.search).map(([chave, valor]) => `${chave}=${encodeURIComponent(String(valor))}`)
  return partes.length === 0 ? destino.to : `${destino.to}?${partes.join('&')}`
}

/** O endereço em que a tela está, lido do roteador. */
export interface LocalAtual {
  readonly pathname: string
  readonly search: Readonly<Record<string, unknown>>
  readonly hash: string
}

/**
 * O registro DA PRÓPRIA linha de listagem (D-155, DS-569): `ficha` navega para outra tela ou
 * vista e empilha histórico; `gaveta` abre a gaveta ou o modal da mesma página e troca o
 * endereço sem empilhar.
 */
export type AlvoDaLinha = 'ficha' | 'gaveta'

/** O link resolvido: em nova aba (o nome de outro registro, D-136) ou na mesma (o alvo da linha). */
export interface LinkResolvido {
  readonly modo: 'link'
  readonly destino: DestinoDaEntidade
  readonly aba: 'nova' | 'mesma'
  /** Troca o endereço em vez de empilhar: só o alvo da linha que abre a gaveta. */
  readonly substituir: boolean
}

export type ResolucaoDoLink = { readonly modo: 'texto' } | LinkResolvido

/**
 * O coração do componente, na ordem da DS-533 com o passo da DS-569:
 *  1. sem tipo ou sem id: texto;
 *  2. dentro de uma linha de listagem, e sem ser o alvo dela: texto (a linha é o alvo, e o
 *     nome de outro registro dentro dela não é link, D-155);
 *  3. sem `ver` no módulo da ficha, ou sem os valores que ela exige: texto (falha fechada,
 *     como `usePode` enquanto o `/me` não chegou; DS-638);
 *  4. o destino é a própria página: texto (a ficha não se linka);
 *  5. senão, link: em nova aba (D-136), ou na mesma quando é o alvo da linha.
 */
export function resolverLinkDaEntidade({
  tipo,
  id,
  pode,
  veValores,
  local,
  escopoDoOrcamento,
  dentroDeLinha = false,
  alvoDaLinha,
}: {
  readonly tipo: TipoDaEntidade | null
  readonly id: string | null | undefined
  readonly pode: (modulo: Modulo, acao: Acao) => boolean
  /** Vê os valores do módulo? Só a ficha que os exige pergunta; ausente = não vê (falha fechada). */
  readonly veValores?: ((modulo: Modulo) => boolean) | undefined
  readonly local: LocalAtual
  readonly escopoDoOrcamento?: 'gastos' | 'financeiro' | undefined
  /** Lido do contexto que a `LinhaDaListagem` provê e que `Modal` e `Gaveta` zeram. */
  readonly dentroDeLinha?: boolean | undefined
  readonly alvoDaLinha?: AlvoDaLinha | undefined
}): ResolucaoDoLink {
  const destino = destinoDaEntidade(tipo, id, { escopoDoOrcamento })
  if (tipo === null || destino === null) return { modo: 'texto' }
  if (dentroDeLinha && alvoDaLinha === undefined) return { modo: 'texto' }
  const mapa = DESTINOS[tipo]
  if (!pode(mapa.modulo, 'ver')) return { modo: 'texto' }
  // A ficha que exige valores no servidor recusaria quem não os vê: o link seria morto.
  if (mapa.valores === true && !(veValores?.(mapa.modulo) ?? false)) return { modo: 'texto' }
  if (ehAPropriaFicha(mapa, destino, local)) return { modo: 'texto' }
  if (alvoDaLinha === undefined) return { modo: 'link', destino, aba: 'nova', substituir: false }
  return { modo: 'link', destino, aba: 'mesma', substituir: alvoDaLinha === 'gaveta' }
}

function ehAPropriaFicha(mapa: DestinoDoMapa, destino: DestinoDaEntidade, local: LocalAtual): boolean {
  if (local.pathname !== destino.to) return false
  // O roteador entrega a âncora sem `#`, e o navegador com: as duas são a mesma ficha.
  if (mapa.chave === 'hash') return local.hash.replace(/^#/, '') === destino.hash
  if (local.search[mapa.chave] !== destino.search[mapa.chave]) return false
  return mapa.fixo === undefined || local.search['cat'] === mapa.fixo.cat
}

/** Os tons do link de entidade. Nenhum sublinha, e nenhum declara `hover:` (D-132). */
export type TomDoLink = 'navy' | 'herdado'

/**
 * As classes do link, pelo sistema do item 08 (`dv-link`, DS-528):
 *  - `navy`: `text-navy dv-link`, o link navy que clareia no hover (ficha, cartão, frase);
 *  - `herdado`: `dv-link dv-link-suave`, que não pinta o repouso (fica a cor da célula) e
 *    vira navy no hover (célula de lista, sublinha neutra);
 *  - sobre `bg-ink`/`bg-navy`, `dv-link dv-link-sobre-escuro`, qualquer que seja o tom: o
 *    navy ali some, e o hover e o anel ficam brancos (EI-31).
 */
export function classesDoLink(tom: TomDoLink, sobreEscuro = false): string {
  if (sobreEscuro) return 'dv-link dv-link-sobre-escuro'
  return tom === 'navy' ? 'text-navy dv-link' : 'dv-link dv-link-suave'
}

/**
 * Nova aba SEMPRE fora do alvo da linha (D-136, D-155), sem dar à aba nova acesso à que a
 * abriu.
 */
export const ATRIBUTOS_DE_NOVA_ABA = { target: '_blank', rel: 'noopener noreferrer' } as const

/** O id do `<span hidden>` do shell que diz "abre em nova aba". */
export const ID_DA_DESCRICAO_NOVA_ABA = 'dv-descricao-nova-aba'

/** O que o link em nova aba espalha: o destino em outra aba e a descrição para o leitor de tela. */
export interface AtributosDeNovaAba {
  readonly target: '_blank'
  readonly rel: 'noopener noreferrer'
  readonly 'aria-describedby': typeof ID_DA_DESCRICAO_NOVA_ABA
}

/** O que o alvo da linha espalha: a marca que a linha lê, e se troca o endereço. */
export interface AtributosDoAlvoDaLinha {
  readonly 'data-alvo-da-linha': ''
  readonly replace: boolean
}

/**
 * Os atributos do link resolvido (D-155, DS-569). O componente só os espalha, e por isso é
 * aqui, sem DOM, que se prova a diferença entre as duas abas:
 *  - nova aba: `target`, `rel` e a descrição única do shell;
 *  - mesma aba (o alvo da linha): a marca `data-alvo-da-linha` e o `replace` da gaveta, sem
 *    `target`, sem `rel` e sem a descrição, que mentiria.
 */
export function atributosDoLink(resolucao: LinkResolvido): AtributosDeNovaAba | AtributosDoAlvoDaLinha {
  if (resolucao.aba === 'nova') {
    return { ...ATRIBUTOS_DE_NOVA_ABA, 'aria-describedby': ID_DA_DESCRICAO_NOVA_ABA }
  }
  return { 'data-alvo-da-linha': '', replace: resolucao.substituir }
}

// ------------------------------------------------------------------- adaptadores

/** O que quem chama espalha no componente: `<LinkDaEntidade {...deParte(x)}>`. Nunca `null`. */
export interface AlvoDoLink {
  readonly tipo: TipoDaEntidade | null
  readonly id: string | null
}

const NENHUM: AlvoDoLink = { tipo: null, id: null }

/** Uma parte de venda: a empresa primeiro, o contato (pessoa física) depois. */
export function deParte(parte: { readonly empresaId: string | null; readonly contatoId: string | null } | null | undefined): AlvoDoLink {
  if (parte === null || parte === undefined) return NENHUM
  if (!vazio(parte.empresaId)) return { tipo: 'empresa', id: parte.empresaId }
  if (!vazio(parte.contatoId)) return { tipo: 'contato', id: parte.contatoId }
  return NENHUM
}

const CHAVE_DO_PARCEIRO = /^(empresa|contato):([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i

/**
 * A chave do parceiro comissionado, `empresa:<uuid>` ou `contato:<uuid>`, que é a forma da
 * comissão e do materializador do portal. Qualquer outra forma é texto.
 */
export function deChaveDoParceiro(chave: string | null | undefined): AlvoDoLink {
  const achado = CHAVE_DO_PARCEIRO.exec(chave ?? '')
  if (achado === null) return NENHUM
  const [, tipo, id] = achado
  if (id === undefined) return NENHUM
  return { tipo: tipo?.toLowerCase() === 'empresa' ? 'empresa' : 'contato', id }
}

/** O vínculo da tarefa usa o nome da tabela: `pedido_venda` é o pedido. */
export function deTipoDeVinculo(tipo: TipoDeVinculoDaTarefa): TipoDaEntidade {
  return tipo === 'pedido_venda' ? 'pedido' : tipo
}

/** A busca global já fala a língua do mapa. */
export function deTipoDaBusca(tipo: TipoDeResultadoDaBusca): TipoDaEntidade {
  return tipo
}

/**
 * A trilha de auditoria guarda o nome da TABELA. Só vira link o tipo cujo id é o do próprio
 * registro com ficha; o resto é texto, e a coluna continua mostrando tipo e identificador:
 *  - `capacidade_fornecedor` é ambíguo, porque a trilha grava com esse tipo tanto o id da
 *    capacidade quanto o da empresa ou do contato dono;
 *  - `conta_contabil` continua texto: a âncora do plano aceita o uuid desde a v1.4.0 (DS-604),
 *    e abrir a trilha por ele é melhoria registrada, não feita (BACKLOG 4.88);
 *  - tipo FILHO de outro (item do pedido, parte do negócio) não abre a ficha do pai: abrir "o
 *    registro mais próximo" seria a resolução silenciosa para outro registro que o
 *    GLOSSARIO proíbe.
 */
const DA_AUDITORIA: Readonly<Record<string, TipoDaEntidade>> = {
  empresa: 'empresa',
  contato: 'contato',
  negocio: 'negocio',
  pedido_venda: 'pedido',
  campanha: 'campanha',
  tarefa: 'tarefa',
  colaborador: 'colaborador',
  conta_pagar: 'conta_pagar',
  orcamento_versao: 'orcamento_versao',
  nf: 'nf',
  regra_comissao: 'regra_comissao',
  entidade_dv: 'entidade_dv',
  conta_bancaria: 'conta_bancaria',
  portal_externo: 'portal_externo',
}

export function deTipoDaAuditoria(tipo: string): TipoDaEntidade | null {
  return Object.hasOwn(DA_AUDITORIA, tipo) ? (DA_AUDITORIA[tipo] ?? null) : null
}

/**
 * A notificação aponta o registro pelo tipo que o emissor gravou. Fila e categoria (as
 * parcelas a aprovar, a cobrança) não são ficha: a inbox as resolve pelo destino sem id.
 */
const DA_NOTIFICACAO: Readonly<Record<string, TipoDaEntidade>> = {
  empresa: 'empresa',
  contato: 'contato',
  negocio: 'negocio',
  pedido: 'pedido',
  pedido_venda: 'pedido',
  campanha: 'campanha',
  tarefa: 'tarefa',
  capacidade_fornecedor: 'fornecedor',
  colaborador: 'colaborador',
  orcamento_versao: 'orcamento_versao',
  conta_bancaria: 'conta_bancaria',
  nf: 'nf',
  // A rejeição de uma CAP volta a quem a registrou, na ficha da 6.4 (DS-637).
  conta_pagar: 'conta_pagar',
}

export function deTipoDaNotificacao(tipo: string): TipoDaEntidade | null {
  return Object.hasOwn(DA_NOTIFICACAO, tipo) ? (DA_NOTIFICACAO[tipo] ?? null) : null
}

/** A referência de ficha do contrato (`referencias.ts`), espalhada como as outras. */
export function deReferencia(referencia: ReferenciaDeFicha | null | undefined): AlvoDoLink {
  if (referencia === null || referencia === undefined) return NENHUM
  return { tipo: referencia.tipo, id: referencia.id }
}
